/* זיהוי כפילויות בסריקה חוזרת. סדר ההתאמה:
   1. אסמכתא זהה = אותה עסקה. אסמכתא שונה = עסקה אחרת, תמיד.
   2. תאריך + סכום + סוג, וכשקיימים בשני הצדדים גם כרטיס ושם מקורי.
      כל שורה שמורה משמשת להתאמה אחת בלבד: 6 קפה בדוח מול 4 שמורים = 2 חדשים.
   3. עסקה ששמה השתנה בדרך (שטרם נקלטה ← סופית, תשלומים, חו"ל).
   4. אותו סכום עד 3 ימים הפרש ושם דומה: לא מחליטים לבד, מציגים לבדיקה. */
import { S } from '../state.js';
import { mNorm, reverseKeepRuns } from '../../lib/text.js';
import { iso10, cents } from '../../lib/format.js';
import { getTxSrc } from '../tx.js';
import { keyOfISO } from '../periods.js';

const dayDiff = (a, b) => Math.abs((new Date(iso10(a) + 'T12:00:00') - new Date(iso10(b) + 'T12:00:00')) / 86400000);

function compatible(t, it, ignoreName) {
  if ((t.type || 'expense') !== (it.type || 'expense')) return false;
  if (t.ref && it.ref && String(t.ref).trim() !== String(it.ref).trim()) return false;
  const c4 = String(it.card4 || '').replace(/\D/g, '').slice(-4);
  if (t.card4 && c4.length === 4 && t.card4 !== c4) return false;
  if (!ignoreName && t.raw_name && it.raw && mNorm(t.raw_name) !== mNorm(it.raw) && mNorm(reverseKeepRuns(t.raw_name)) !== mNorm(it.raw)) return false;
  return true;
}
function nameClose(t, it) {
  const a = mNorm(t.raw_name || t.description || ''), b = mNorm(it.raw || it.description || '');
  if (!a || !b) return false;
  return a === b || a.indexOf(b) >= 0 || b.indexOf(a) >= 0 || a.slice(0, 3) === b.slice(0, 3);
}

/** השדות לזיהוי כפילויות שנשמרים עם כל פעולה */
export function txExtraFields(it) {
  const o = {};
  if (it.ref) o.ref = String(it.ref).trim().slice(0, 60);
  if (it.raw) o.raw_name = String(it.raw).trim().slice(0, 200);
  const c4 = String(it.card4 || '').replace(/\D/g, '').slice(-4);
  if (c4.length === 4) o.card4 = c4;
  if (it.orig) o.orig_amount = String(it.orig).trim().slice(0, 40);
  return o;
}

/**
 * @param items שורות הסריקה
 * @param src 'cash' | 'credit'
 * @param selKey התקופה שנבחרה לשמירה ('' = לפי תאריך)
 * @returns {{res: Array<{status:'new'|'dup'|'near'|'infile', existing?, twin?, rename?, updateAmount?, updateDate?}>, missing: Array, older: boolean}}
 */
export function scanReconcile(items, src, today, selKey) {
  const pool = S.tx.filter((t) => getTxSrc(t) === src);
  const used = {};
  const res = items.map(() => ({ status: 'new' }));
  const dt = (it) => iso10(it.date || today);
  const take = (i, m, extra) => { used[m.id] = 1; res[i] = Object.assign({ status: 'dup', existing: m }, extra || {}); };

  // 0. כפילות בתוך הדוח: שורה ממתינה (בלי שובר) שיש לה שורה סופית באותו קובץ
  const pairedIn = {};
  items.forEach((it, i) => {
    if (it.ref || it.notYetBilled !== true) return;
    let j = -1;
    items.forEach((o, k) => {
      if (j >= 0 || k === i || !o.ref || pairedIn[k] || (o.type || 'expense') !== (it.type || 'expense')) return;
      const same = iso10(o.date) === dt(it) && cents(o.amount) === cents(it.amount);
      const im = String(o.orig || '').match(/^([\d.]+) ILS@(\d{4}-\d{2}-\d{2})$/);
      const inst = im && im[2] === dt(it) && cents(im[1]) === cents(it.amount);
      if (same || inst) j = k;
    });
    if (j >= 0) { pairedIn[j] = 1; res[i] = { status: 'infile', twin: items[j] }; }
  });
  // 1. אסמכתא
  items.forEach((it, i) => {
    if (!it.ref || res[i].status !== 'new') return;
    const r = String(it.ref).trim();
    const m = pool.find((t) => !used[t.id] && t.ref && String(t.ref).trim() === r && (t.type || 'expense') === (it.type || 'expense') &&
      dayDiff(t.date, dt(it)) <= 3 && compatible(t, it, true));
    if (m) take(i, m);
  });
  // 2. תאריך + סכום, עם ספירה
  items.forEach((it, i) => {
    if (res[i].status !== 'new') return;
    const m = pool.find((t) => !used[t.id] && iso10(t.date) === dt(it) && cents(t.amount) === cents(it.amount) && compatible(t, it));
    if (m) take(i, m);
  });
  // 2ב. "שטרם נקלטה" (בלי שובר) מול אותה עסקה סופית (עם שובר). השם משתנה בדרך (חברת סליקה, חיתוך ל-20 תווים)
  items.forEach((it, i) => {
    if (res[i].status !== 'new') return;
    const m = pool.find((t) => !used[t.id] && !!t.ref !== !!it.ref && iso10(t.date) === dt(it) && cents(t.amount) === cents(it.amount) && compatible(t, it, true));
    // רק כשהחדשה סופית מעדכנים את השמורה. דוח ישן לא דורס מידע חדש
    if (m) take(i, m, it.ref ? { rename: true } : {});
  });
  // 2ג. תשלומים: כשטרם נקלטה, העסקה בתאריך הרכישה ובסכום המלא; בקליטה כתשלום 1 מתוך N
  items.forEach((it, i) => {
    if (res[i].status !== 'new') return;
    const im = String(it.orig || '').match(/^([\d.]+) ILS@(\d{4}-\d{2}-\d{2})$/);
    if (im && it.ref) {
      const m = pool.find((t) => !used[t.id] && !t.ref && iso10(t.date) === im[2] && cents(t.amount) === cents(im[1]) && compatible(t, it, true));
      if (m) { take(i, m, { rename: true, updateAmount: true, updateDate: true }); return; }
    }
    if (!it.ref) {
      const key = Math.abs(Number(it.amount) || 0) + ' ILS@' + dt(it);
      const m = pool.find((t) => !used[t.id] && t.ref && t.orig_amount && t.orig_amount === key && compatible(t, it, true));
      if (m) take(i, m);
    }
  });
  // 3. חו"ל: אותו סכום מקורי, הסכום בשקלים התעדכן
  items.forEach((it, i) => {
    if (res[i].status !== 'new' || !it.orig) return;
    const m = pool.find((t) => !used[t.id] && t.orig_amount && t.orig_amount === String(it.orig).trim() && dayDiff(t.date, dt(it)) <= 2 && compatible(t, it, true));
    if (m) take(i, m, { updateAmount: cents(m.amount) !== cents(it.amount) });
  });
  // 3ב. נשמרה כשטרם נקלטה ועכשיו סופית בסכום שונה: אותו שם מקורי, עד 3 ימים, סכום בטווח 35%
  items.forEach((it, i) => {
    if (res[i].status !== 'new' || !it.ref || !it.raw) return;
    const n = mNorm(it.raw), a = Math.abs(Number(it.amount) || 0);
    const m = pool.find((t) => {
      if (used[t.id] || t.ref || !t.raw_name || mNorm(t.raw_name) !== n) return false;
      if ((t.type || 'expense') !== (it.type || 'expense')) return false;
      const b = Math.abs(Number(t.amount) || 0);
      return dayDiff(t.date, dt(it)) <= 3 && b > 0 && Math.abs(a - b) / Math.max(a, b) <= 0.35;
    });
    if (m) take(i, m, { updateAmount: cents(m.amount) !== cents(it.amount), updateDate: iso10(m.date) !== dt(it) });
  });
  // 4. קרוב: לבדיקה בלבד
  items.forEach((it, i) => {
    if (res[i].status !== 'new') return;
    const m = pool.find((t) => !used[t.id] && cents(t.amount) === cents(it.amount) && compatible(t, it) && dayDiff(t.date, dt(it)) <= 3 && nameClose(t, it));
    if (m) { used[m.id] = 1; res[i] = { status: 'near', existing: m }; }
  });

  // שמורות בטווח הדוח שלא הופיעו בו: רק כשבטוח שהן מאותו כרטיס או חשבון, ומאותה תקופת חיוב
  const missing = [];
  let older = false;
  const dates = items.filter((it) => !it.instNum).map(dt).filter(Boolean).sort();
  if (dates.length) {
    const lo = dates[0], hi = dates[dates.length - 1];
    const cards = {};
    let hasRef = false;
    items.forEach((it) => {
      const c4 = String(it.card4 || '').replace(/\D/g, '').slice(-4);
      if (c4.length === 4) cards[c4] = 1;
      if (it.ref) hasRef = true;
    });
    const months = {};
    items.forEach((it) => (months[it.month || selKey || keyOfISO(dt(it))] = 1));
    const sameSource = (t) => (t.card4 && cards[t.card4]) || (src !== 'credit' && hasRef && t.ref);
    // דוח ישן יותר ממה שכבר נסרק: עסקאות שנקלטו אחריו פשוט לא בו, ולכן לא בודקים "חסרות"
    older = pool.some((t) => months[t.month] && sameSource(t) && iso10(t.date) > hi && iso10(t.date) <= today);
    if (!older) {
      pool.forEach((t) => {
        if (used[t.id] || !months[t.month]) return;
        const ds = iso10(t.date);
        if (ds >= lo && ds <= hi && sameSource(t)) missing.push(t);
      });
    }
  }
  return { res, missing, older };
}
